import { useState, useEffect, useRef, useCallback } from 'react';
import { FILTER_TYPES } from './shared';

// ── URL persistence helpers ───────────────────────────────────────────────────
function readFiltersFromUrl() {
  try {
    const raw = new URLSearchParams(window.location.search).get('f');
    return raw ? JSON.parse(decodeURIComponent(raw)) : [];
  } catch {
    return [];
  }
}

function writeFiltersToUrl(filters) {
  const params = new URLSearchParams(window.location.search);
  if (filters.length > 0) {
    params.set('f', encodeURIComponent(JSON.stringify(filters)));
  } else {
    params.delete('f');
  }
  const search = params.toString();
  window.history.replaceState(null, '', window.location.pathname + (search ? '?' + search : ''));
}

// ── Hook ─────────────────────────────────────────────────────────────────────
/**
 * Manages analyzer filter state with two layers:
 *   - `filters`        — draft state (what user is editing in UI)
 *   - `appliedFilters` — committed state (what actually triggers search)
 *
 * Call `applyFilters()` to copy draft → applied (triggers search).
 * `hasPendingChanges` is true when draft differs from applied.
 */
export function useAnalyzerFilters() {
  const initial = readFiltersFromUrl();
  const [filters, setFilters]               = useState(initial);
  const [appliedFilters, setAppliedFilters] = useState(initial);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // filtersRef always points to appliedFilters (used by buildFilterParams in StrategyAnalyzer)
  const filtersRef = useRef(appliedFilters);
  // Update ref synchronously so buildFilterParams always gets the latest applied value
  filtersRef.current = appliedFilters;

  // Sync applied filters to URL
  useEffect(() => {
    writeFiltersToUrl(appliedFilters);
  }, [appliedFilters]);

  // Pending = draft differs from applied
  const hasPendingChanges = JSON.stringify(filters) !== JSON.stringify(appliedFilters);

  // Apply draft → applied (this is what triggers the search in StrategyAnalyzer)
  const applyFilters = useCallback(() => {
    setAppliedFilters([...filters]);
  }, [filters]);

  const addFilter = useCallback((field) => {
    const ft = FILTER_TYPES.find(t => t.value === field);
    if (!ft) return;
    setFilters(prev => [...prev, {
      id: Date.now(), field, operator: 'and', type: ft.type,
      from: null, to: null, datePreset: null, value: null,
    }]);
    setShowFilterPanel(true);
  }, []);

  const removeFilter = useCallback((id) => {
    setFilters(prev => prev.filter(f => f.id !== id));
    setAppliedFilters(prev => prev.filter(f => f.id !== id));
  }, []);

  const updateFilter = useCallback((id, changes, applyImmediately = false) => {
    if (applyImmediately) {
      setFilters(prev => {
        const next = prev.map(f => f.id === id ? { ...f, ...changes } : f);
        setAppliedFilters(next);
        return next;
      });
    } else {
      setFilters(prev => prev.map(f => f.id === id ? { ...f, ...changes } : f));
    }
  }, []);

  const toggleOperator = useCallback((id) => {
    setFilters(prev => prev.map(f =>
      f.id === id ? { ...f, operator: f.operator === 'and' ? 'or' : 'and' } : f
    ));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters([]);
    setAppliedFilters([]);
  }, []);

  const setFiltersDirect = useCallback((newFilters) => {
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  }, []);

  return {
    filters,           // draft (UI editing)
    appliedFilters,    // committed (triggers search)
    filtersRef,        // ref to appliedFilters for use in callbacks
    hasPendingChanges,
    showFilterPanel,
    setShowFilterPanel,
    addFilter,
    removeFilter,
    updateFilter,
    toggleOperator: toggleOperator,
    resetFilters,
    applyFilters,
    setFiltersDirect,
  };
}
